import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  HelpCircle, 
  ShoppingCart, 
  Search, 
  Package, 
  Tag, 
  Clock, 
  ChevronDown, 
  ChevronUp,
  LogIn,
  FileText,
  MessageSquare,
  Phone
} from 'lucide-react';

const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Card className="border-slate-200">
      <CardHeader 
        className="cursor-pointer hover:bg-slate-50 transition-colors py-4"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CardTitle className="flex items-center justify-between text-base md:text-lg">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-blue-600" />
            {title}
          </div>
          {isOpen ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
        </CardTitle>
      </CardHeader>
      {isOpen && (
        <CardContent className="pt-0 pb-4">
          {children}
        </CardContent>
      )}
    </Card>
  );
};

const Step = ({ number, title, children }) => (
  <div className="flex gap-3 mb-4">
    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
      {number}
    </div>
    <div className="flex-1">
      <p className="font-semibold text-slate-900 mb-1">{title}</p>
      <p className="text-slate-600 text-sm">{children}</p>
    </div>
  </div>
);

const FAQ = ({ question, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        className="w-full py-3 flex items-start justify-between text-left hover:bg-slate-50 transition-colors px-2 -mx-2 rounded"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-slate-900 pr-4">{question}</span>
        {isOpen ? <ChevronUp className="h-5 w-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />}
      </button>
      {isOpen && (
        <div className="pb-3 px-2 -mx-2 text-sm text-slate-600">
          {children}
        </div>
      )}
    </div>
  );
};

export const CustomerHelp = () => {
  return (
    <div className="space-y-4 md:space-y-6 max-w-3xl mx-auto" data-testid="customer-help">
      <div className="text-center mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center justify-center gap-2">
          <HelpCircle className="h-6 w-6 text-blue-600" />
          やさまるFish ご利用ガイド
        </h1>
        <p className="text-slate-500 mt-2 text-sm">注文方法やよくある質問をまとめました</p>
      </div>

      {/* ログイン方法 */}
      <Section title="ログイン方法" icon={LogIn} defaultOpen={true}>
        <div className="space-y-4">
          <Step number="1" title="ログイン画面にアクセス">
            お送りしたURLにアクセスしてください。
          </Step>
          <Step number="2" title="ログインID・パスワードを入力">
            お知らせしたログインIDとパスワードを入力してください。
          </Step>
          <Step number="3" title="「ログイン」ボタンをタップ">
            入力が完了したら「ログイン」ボタンを押してください。
          </Step>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
            <p className="text-amber-800 text-sm">
              <strong>ログインできない場合：</strong><br />
              パスワードをお忘れの場合は、担当者までご連絡ください。
            </p>
          </div>
        </div>
      </Section>

      {/* 商品の探し方 */}
      <Section title="商品の探し方" icon={Search}>
        <div className="space-y-4">
          <div className="mb-4">
            <h4 className="font-semibold text-slate-900 mb-2">検索バーで探す</h4>
            <p className="text-slate-600 text-sm">
              画面上部の検索バーに商品名、産地、規格などを入力すると、該当する商品だけが表示されます。
            </p>
          </div>
          
          <div className="mb-4">
            <h4 className="font-semibold text-slate-900 mb-2">カテゴリで絞り込む</h4>
            <p className="text-slate-600 text-sm mb-2">
              検索バーの下にあるボタンで、カテゴリ別に商品を絞り込めます。
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-blue-100 text-blue-800">すべて</Badge>
              <Badge className="bg-amber-100 text-amber-800">⭐ AI提案</Badge>
              <Badge className="bg-red-100 text-red-800">特売</Badge>
              <Badge className="bg-slate-100 text-slate-800">魚介類</Badge>
              <Badge className="bg-slate-100 text-slate-800">野菜</Badge>
              <Badge className="bg-slate-100 text-slate-800">など</Badge>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-blue-800 text-sm">
              <strong>⭐ AI提案とは？</strong><br />
              お客様の注文履歴をもとに、おすすめの商品を表示しています。
            </p>
          </div>
        </div>
      </Section>

      {/* 注文方法 */}
      <Section title="注文方法" icon={ShoppingCart}>
        <div className="space-y-4">
          <Step number="1" title="商品を選ぶ">
            商品カタログから欲しい商品を探します。
          </Step>
          <Step number="2" title="数量を選択">
            商品カードの「＋」「－」ボタンで数量を調整できます。
          </Step>
          <Step number="3" title="カートに追加">
            「カートに追加」または「追加」ボタンをタップします。
          </Step>
          <Step number="4" title="カートを確認">
            画面右上のカートアイコンをタップして、注文内容を確認します。
          </Step>
          <Step number="5" title="配送希望日を選択">
            カート画面で配送希望日を選んでください（必須）。
          </Step>
          <Step number="6" title="注文を送信">
            内容を確認して「注文を送信」ボタンをタップで完了です。
          </Step>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-4">
            <p className="text-emerald-800 text-sm">
              <strong>注文完了後：</strong><br />
              注文履歴ページでいつでも注文内容を確認できます。
            </p>
          </div>
        </div>
      </Section>

      {/* 特売商品について */}
      <Section title="特売商品について" icon={Tag}>
        <div className="space-y-3 text-sm text-slate-600">
          <p>
            <strong className="text-red-600">特売商品</strong>は、赤い枠と「特売」バッジで表示されます。
          </p>
          <p>
            特売商品は日によって内容が変わります。毎日チェックしてお得な商品を見つけてください！
          </p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
            <p className="text-red-800">
              <strong>「特売準備中」と表示されている場合：</strong><br />
              特売商品の準備中です。しばらくお待ちください。
            </p>
          </div>
        </div>
      </Section>

      {/* 価格について */}
      <Section title="価格について" icon={Package}>
        <div className="space-y-3 text-sm text-slate-600">
          <p>
            商品の価格は<strong>kg単価</strong>で表示しています（パック売り商品を除く）。
          </p>
          <p>
            カートの金額は<strong>目安金額</strong>です。計量後に実際の金額が確定します。
          </p>
          <p>
            「<strong>時価</strong>」と表示されている商品は、市場価格により変動します。
          </p>
        </div>
      </Section>

      {/* 配送について */}
      <Section title="配送について" icon={Clock}>
        <div className="space-y-3 text-sm text-slate-600">
          <p>
            <strong>配送希望日</strong>は注文時に必ず選択してください。
          </p>
          <p>
            翌日配送の注文締め切りは<strong>当日23:59まで</strong>です。
          </p>
          <p>
            休業日は配送希望日として選択できません。
          </p>
        </div>
      </Section>

      {/* 見積もり依頼 */}
      <Section title="見積もり依頼" icon={FileText}>
        <div className="space-y-3 text-sm text-slate-600">
          <p>
            カタログにない商品をお探しの場合は、「見積もり依頼」からリクエストできます。
          </p>
          <Step number="1" title="メニューから「見積もり依頼」を選択">
            画面上部のナビゲーションから選んでください。
          </Step>
          <Step number="2" title="必要事項を入力">
            お探しの商品名、希望サイズ、希望価格などを入力します。
          </Step>
          <Step number="3" title="送信">
            内容を確認して送信してください。担当者から折り返しご連絡いたします。
          </Step>
        </div>
      </Section>

      {/* よくある質問 */}
      <Section title="よくある質問（FAQ）" icon={MessageSquare}>
        <div className="space-y-1">
          <FAQ question="ログインIDやパスワードを忘れました">
            担当者までご連絡ください。新しいパスワードを発行いたします。
          </FAQ>
          
          <FAQ question="注文した後にキャンセルや変更はできますか？">
            注文後のキャンセル・変更は、お電話またはLINEで担当者までご連絡ください。
            配送準備に入っている場合は対応できない場合がございます。
          </FAQ>
          
          <FAQ question="配送時間の指定はできますか？">
            配送時間の指定については、担当者までご相談ください。
            ルートの都合上、ご希望に添えない場合もございます。
          </FAQ>
          
          <FAQ question="注文履歴はどこで確認できますか？">
            画面上部の「注文履歴」メニューから、過去の注文内容を確認できます。
          </FAQ>
          
          <FAQ question="商品の画像が「画像準備中」になっています">
            画像の準備ができていない商品です。商品名・規格・産地をご確認の上、ご注文ください。
          </FAQ>
          
          <FAQ question="カタログに欲しい商品がありません">
            「見積もり依頼」からリクエストいただくか、担当者までご連絡ください。
            可能な限りお探しいたします。
          </FAQ>
          
          <FAQ question="スマートフォンでも注文できますか？">
            はい、スマートフォン・タブレット・パソコンからご注文いただけます。
          </FAQ>
        </div>
      </Section>

      {/* お問い合わせ */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Phone className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900">お問い合わせ</h3>
              <p className="text-blue-800 text-sm mt-1">
                ご不明な点がございましたら、お気軽に担当者までお問い合わせください。
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="text-center text-xs text-slate-400 pt-4">
        やさまるFish ご利用ガイド
      </p>
    </div>
  );
};
